/* eslint-disable jsx-a11y/click-events-have-key-events */
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import React from "react";

import Arrow from "../Arrow";

dayjs.extend(utc);
dayjs.extend(isoWeek);

const getShortWeekdayNames = (locale: string) => {
  const days = [];
  for (let d = new Date(2023, 5, 12), i = 7; i; i -= 1) {
    days.push(d.toLocaleString(locale, { weekday: "short" }));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

interface DayViewProps {
  month: number;
  year: number;
  onPreviousMonth?: () => void;
  onNextMonth?: () => void;
  onClickMonth?: () => void;

  // Display of dates independent
  onHoverDate?: (date: Date) => void;
  onSelectDate?: (date: Date) => void;
  selected?: Date | Date[];
  showOutsideDays?: boolean;

  minDate?: Date;
  maxDate?: Date;
}

/** Operates entirely in UTC */
const DayView: React.FC<DayViewProps> = ({
  month, year, onPreviousMonth, onNextMonth,
  onHoverDate, onSelectDate, selected, onClickMonth,
  showOutsideDays = true,
  minDate, maxDate,
}) => {
  // Get user's locale
  const locale = navigator.language;
  const days = getShortWeekdayNames(locale).map((s) => s.replace(/[^A-Za-z]/g, ""));

  // Get the first day of the month
  const firstDay = dayjs(Date.UTC(year, month - 1, 1)).startOf("day");

  // Get the last day of the month
  const lastDay = dayjs(Date.UTC(year, month, 0)).startOf("day");

  const firstCalendarDate = firstDay.startOf("isoWeek");
  const lastCalendarDate = lastDay.endOf("isoWeek");

  return (
    <div>
      <div className="flex flex-row items-center pb-4 pt-2 select-none">
        <span className="flex-1">
          {onPreviousMonth && <Arrow direction="left" onClick={onPreviousMonth} disabled={!(!minDate || dayjs.utc(Date.UTC(year, month - 1, 1)).isAfter(dayjs.utc(minDate).startOf("day")))} />}
        </span>
        <span
          className="typography-medium-base flex-4 text-center capitalize"
        >
          <span onClick={onClickMonth} className={`${onClickMonth ? "cursor-pointer" : ""}`}>
            {firstDay.toDate().toLocaleString(locale, { month: "long", year: "numeric" })}
          </span>
        </span>
        <span className="flex-1 flex justify-end">
          {onNextMonth && <Arrow direction="right" onClick={onNextMonth} disabled={!(!maxDate || dayjs.utc(Date.UTC(year, month, 1)).isBefore(dayjs.utc(maxDate).startOf("day")))} />}
        </span>
      </div>
      <div
        className="grid grid-cols-7 grid-rows-7 select-none"
      >
        {/* Locale formatted days of week */}
        {days.map((day) => (
          <div key={day} className="typography-small-base capitalize text-text-subtle text-center p-2 flex justify-center items-center">{day}</div>
        ))}
        {
          Array.from({ length: lastCalendarDate.diff(firstCalendarDate, "day") + 1 }, (_, i) => {
            const date = firstCalendarDate.add(i, "day");
            const isCurrentMonth = date.isSame(firstDay, "month");
            const isToday = date.isSame(dayjs(), "day");

            const selectedRange = Array.isArray(selected) ? selected : [selected, selected];
            const isStartOfRange = selectedRange && selectedRange[0] && date.isSame(selectedRange[0], "day");
            const isMiddleOfRange = selectedRange && selectedRange[0] && selectedRange[1] && date.isAfter(selectedRange[0], "day") && date.isBefore(selectedRange[1], "day");
            const isEndOfRange = selectedRange && selectedRange[1] && date.isSame(selectedRange[1], "day");
            const isRelevant = isStartOfRange || isMiddleOfRange || isEndOfRange;
            const disabled = (minDate && date.isBefore(dayjs(minDate).startOf("day")))
              || (maxDate && date.isAfter(dayjs(maxDate).endOf("day")));

            if (!showOutsideDays && (date.isBefore(firstDay) || date.isAfter(lastDay))) {
              return <div key={date.format("YYYY-MM-DD")} className="text-text-subtle" />;
            }

            return (
              <div
                key={date.format("YYYY-MM-DD")}
                className={
                  `typography-small-base text-center p-2 m-1 select-none w-8 h-8
                  ${isCurrentMonth && !disabled ? "text-text-base hover:cursor-pointer" : "text-text-disabled"}
                  ${isToday && !isRelevant ? "outline-1 outline-border-strong bg-bg-components-base" : ""}
                  ${!isRelevant ? "rounded-md" : ""}
                  ${!disabled && !isRelevant ? "hover:bg-bg-components-hover active:bg-bg-components-pressed" : ""}
                  ${!disabled && isStartOfRange ? "bg-bg-interactive-base rounded-l-md text-text-on-color hover:bg-bg-interactive-hover" : ""}
                  ${!disabled && isMiddleOfRange ? "bg-bg-interactive-light text-text-interactive-base rounded-md" : ""}
                  ${!disabled && isEndOfRange ? "bg-bg-interactive-base rounded-r-md text-text-on-color hover:bg-bg-interactive-hover" : ""}
                  `
                }
                onClick={!disabled ? () => onSelectDate && onSelectDate(date.toDate()) : undefined}
                onMouseEnter={!disabled ? () => onHoverDate && onHoverDate(date.toDate()) : undefined}
              >
                {date.date()}
              </div>
            );
          })
        }
      </div>
    </div>
  );
};

export default DayView;
